import { useEffect, useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  ArchiveRestore,
  CheckCircle2,
  ExternalLink,
  Plus,
  RotateCcw,
  Send,
  Trash2,
  Unlink,
  XCircle,
} from 'lucide-react'
import {
  Button,
  Chip,
  EmptyState,
  PartnerPicker,
  toast,
} from '@/components/ui'
import type { ButtonVariant } from '@/components/ui/Button/Button'
import type { PickerValue } from '@/components/ui/ProductPicker/ProductPicker'
import { ApiError } from '@/lib/api/jsonFetch'
import { odooUrl } from '@/lib/utils/odoo-url'
import {
  useCreateProject,
  useDeleteProject,
  useLinkFormulation,
  useLinkSample,
  useProject,
  useRestoreProject,
  useSuggestedSamples,
  useTransitionProject,
  useUnlinkFormulation,
  useUnlinkSample,
  useUpdateProject,
} from '../api/queries'
import type { ProjectStatus, ProjectSummary } from '../api/types'
import { FormulationPicker } from './FormulationPicker'
import { ProjectStatusChip } from './ProjectStatusChip'
import { ProjectTransitionDialog } from './ProjectTransitionDialog'

/**
 * ProjectDetail — Sprint 10 Chunk 4 full implementation.
 *
 * Three modes:
 *   1. Create (`projectId === null`) — New project form: customer
 *      (PartnerPicker), name, description. Save → calls onCreated
 *      with the new id so the parent can select it in the list.
 *   2. Edit (`projectId !== null`, data loaded) — Header form +
 *      transition buttons + linked-formulations + sample-orders +
 *      status history. Customer/name/description are saved via
 *      explicit Save button (not optimistic) — projects move
 *      slower than formulations and a confirmation prompt feels
 *      lighter on the eyes.
 *   3. Loading — header from `summary` prop while detail query runs.
 *
 * D-3 (auto-detect + manual confirm): suggested samples come from
 * `useSuggestedSamples`; user clicks Link to persist via
 * `useLinkSample`. Both sections render when the project has a
 * customer; "no customer" path shows a hint.
 */

interface TransitionAction {
  to: ProjectStatus
  label: string
  variant: ButtonVariant
  icon: typeof CheckCircle2
}

function transitionsFor(status: ProjectStatus): TransitionAction[] {
  switch (status) {
    case 'draft':
      return [
        { to: 'testing', label: 'Submit for testing', variant: 'primary', icon: Send },
        { to: 'rejected', label: 'Reject', variant: 'danger', icon: XCircle },
      ]
    case 'testing':
      return [
        { to: 'approved', label: 'Approve', variant: 'primary', icon: CheckCircle2 },
        { to: 'rejected', label: 'Reject', variant: 'danger', icon: XCircle },
        { to: 'draft', label: 'Back to draft', variant: 'secondary', icon: RotateCcw },
      ]
    case 'approved':
      return [
        { to: 'draft', label: 'Reopen', variant: 'secondary', icon: RotateCcw },
        { to: 'rejected', label: 'Reject', variant: 'danger', icon: XCircle },
      ]
    case 'rejected':
      return [
        { to: 'draft', label: 'Revive to draft', variant: 'secondary', icon: RotateCcw },
      ]
  }
}

export interface ProjectDetailProps {
  /** When `null`, renders the create form. */
  projectId: number | null
  /** Optional summary from the master list — header fallback during
   *  the detail query. */
  summary?: ProjectSummary
  onCreated?: (newId: number) => void
  onCancelCreate?: () => void
}

export function ProjectDetail({
  projectId,
  summary,
  onCreated,
  onCancelCreate,
}: ProjectDetailProps) {
  const isCreateMode = projectId === null

  const { data, isLoading, isError } = useProject(projectId)
  const createMut = useCreateProject()
  const updateMut = useUpdateProject()
  const transitionMut = useTransitionProject()
  const archiveMut = useDeleteProject()
  const restoreMut = useRestoreProject()
  const linkFormulationMut = useLinkFormulation()
  const unlinkFormulationMut = useUnlinkFormulation()
  const linkSampleMut = useLinkSample()
  const unlinkSampleMut = useUnlinkSample()
  const { data: suggestedSamples } = useSuggestedSamples(
    isCreateMode ? null : projectId,
  )

  // ─── Form draft state ───────────────────────────────────────────────
  // Create + edit paths both maintain a local draft so Save can be
  // explicit. Edit path syncs from `data` whenever the loaded project
  // changes; create path resets to a blank draft when projectId=null.

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [customer, setCustomer] = useState<PickerValue>(null)

  useEffect(() => {
    if (isCreateMode) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setName('')
       
      setDescription('')
       
      setCustomer(null)
      return
    }
    if (data) {
       
      setName(data.name)
       
      setDescription(data.description ?? '')
      // We don't have the customer name from `/api/projects/{id}`
      // (only customer_id), so seed the picker with a `[id, '#id']`
      // synthetic label until the user re-picks. PartnerPicker
      // re-fetches on focus so the real name surfaces on first edit.
       
      setCustomer(
        data.customer_id !== null
          ? [data.customer_id, `customer #${data.customer_id}`]
          : null,
      )
    }
  }, [data, isCreateMode])

  // Transition dialog state
  const [transitionTarget, setTransitionTarget] = useState<TransitionAction | null>(null)

  // Formulation picker dialog state
  const [pickerOpen, setPickerOpen] = useState(false)

  // ─── Derived ───────────────────────────────────────────────────────

  const project = data
  const headerProject = project ?? summary
  const isArchived = Boolean(project?.deleted_at)

  const linkedFormulationIds = useMemo(
    () => new Set((project?.formulations ?? []).map((f) => f.id)),
    [project],
  )

  const isHeaderDirty = useMemo(() => {
    if (isCreateMode) return name.trim() !== '' || description.trim() !== '' || customer !== null
    if (!project) return false
    return (
      name !== project.name ||
      (description || null) !== (project.description ?? null) ||
      (customer ? customer[0] : null) !== project.customer_id
    )
  }, [isCreateMode, project, name, description, customer])

  // ─── Handlers ──────────────────────────────────────────────────────

  function handleSave() {
    const trimmedName = name.trim()
    if (!trimmedName) {
      toast.danger('Project name is required.')
      return
    }
    const body = {
      name: trimmedName,
      description: description.trim() || null,
      customer_id: customer ? customer[0] : null,
    }
    if (isCreateMode) {
      createMut.mutate(body, {
        onSuccess: (created) => {
          toast.success(`Created "${created.name}"`)
          onCreated?.(created.id)
        },
        onError: (err) => {
          const msg = err instanceof ApiError ? err.message : 'Create failed'
          toast.danger(msg)
        },
      })
      return
    }
    if (projectId === null) return
    updateMut.mutate(
      { id: projectId, body },
      {
        onSuccess: () => toast.success('Project saved'),
        onError: (err) => {
          const msg = err instanceof ApiError ? err.message : 'Save failed'
          toast.danger(msg)
        },
      },
    )
  }

  function handleArchive() {
    if (projectId === null || !project) return
    if (!window.confirm(`Archive project "${project.name}"?`)) return
    archiveMut.mutate(projectId, {
      onSuccess: () => toast.success('Project archived'),
      onError: (err) => {
        const msg = err instanceof ApiError ? err.message : 'Archive failed'
        toast.danger(msg)
      },
    })
  }

  function handleRestore() {
    if (projectId === null) return
    restoreMut.mutate(projectId, {
      onSuccess: () => toast.success('Project restored'),
      onError: (err) => {
        const msg = err instanceof ApiError ? err.message : 'Restore failed'
        toast.danger(msg)
      },
    })
  }

  function handleTransitionConfirm(comment: string | null) {
    if (!transitionTarget || projectId === null) return
    transitionMut.mutate(
      { id: projectId, body: { to: transitionTarget.to, comment } },
      {
        onSuccess: () => {
          toast.success(
            `Status: ${
              transitionTarget.to.charAt(0).toUpperCase() +
              transitionTarget.to.slice(1)
            }`,
          )
          setTransitionTarget(null)
        },
        onError: (err) => {
          const msg = err instanceof ApiError ? err.message : 'Transition failed'
          toast.danger(msg)
        },
      },
    )
  }

  function handlePickFormulation(formulationId: number) {
    if (projectId === null) return
    linkFormulationMut.mutate(
      { projectId, formulationId },
      {
        onSuccess: () => {
          toast.success('Formulation linked')
          setPickerOpen(false)
        },
        onError: (err) => {
          const msg = err instanceof ApiError ? err.message : 'Link failed'
          toast.danger(msg)
        },
      },
    )
  }

  function handleUnlinkFormulation(formulationId: number, label: string) {
    if (projectId === null) return
    if (!window.confirm(`Unlink formulation ${label} from this project?`)) return
    unlinkFormulationMut.mutate(
      { projectId, formulationId },
      {
        onSuccess: () => toast.success('Formulation unlinked'),
        onError: (err) => {
          const msg = err instanceof ApiError ? err.message : 'Unlink failed'
          toast.danger(msg)
        },
      },
    )
  }

  function handleLinkSample(odooSoId: number) {
    if (projectId === null) return
    linkSampleMut.mutate(
      { projectId, body: { odoo_so_id: odooSoId } },
      {
        onSuccess: () => toast.success(`Linked SO#${odooSoId}`),
        onError: (err) => {
          const msg = err instanceof ApiError ? err.message : 'Link failed'
          toast.danger(msg)
        },
      },
    )
  }

  function handleUnlinkSample(sampleId: number, soId: number) {
    if (projectId === null) return
    if (!window.confirm(`Unlink SO#${soId} from this project?`)) return
    unlinkSampleMut.mutate(
      { projectId, sampleId },
      {
        onSuccess: () => toast.success('Sample order unlinked'),
        onError: (err) => {
          const msg = err instanceof ApiError ? err.message : 'Unlink failed'
          toast.danger(msg)
        },
      },
    )
  }

  // ─── Render: create mode ────────────────────────────────────────────

  if (isCreateMode) {
    return (
      <div className="flex h-full flex-col">
        <header className="border-b border-border-subtle bg-surface-sunk px-6 py-3">
          <h2 className="text-role-page-title leading-tight">New project</h2>
          <p className="text-sm text-secondary">
            One project per customer R&amp;D ask. Link formulations + sample
            orders after saving.
          </p>
        </header>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex max-w-xl flex-col gap-4">
            <FormField label="Customer" required>
              <PartnerPicker
                value={customer}
                onChange={setCustomer}
                kind="customer"
                placeholder="Search customers…"
              />
            </FormField>
            <FormField label="Name" required>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Acme low-VOC moisturizer"
                className="w-full rounded-xs border border-border-default bg-surface px-2 py-1.5 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
              />
            </FormField>
            <FormField label="Description">
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="What's the customer asking for? Constraints, deadlines, sample requirements…"
                className="w-full rounded-xs border border-border-default bg-surface px-2 py-1.5 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
              />
            </FormField>
            <div className="mt-2 flex items-center gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                loading={createMut.isPending}
              >
                Save project
              </Button>
              {onCancelCreate ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={onCancelCreate}
                  disabled={createMut.isPending}
                >
                  Cancel
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ─── Render: edit mode ─────────────────────────────────────────────

  if (isLoading && !headerProject) {
    return <div className="p-6 text-sm text-tertiary">Loading project…</div>
  }
  if (isError) {
    return (
      <div className="p-6">
        <EmptyState
          variant="error"
          title="Couldn't load project"
          description="Reload to retry. If the issue persists, check that the middleware container is up to date."
        />
      </div>
    )
  }
  if (!project) return null

  const formulations = project.formulations ?? []
  const sampleOrders = project.sample_orders ?? []
  const statusHistory = project.status_history ?? []
  const suggestionsList = suggestedSamples?.samples ?? []
  const suggestionsReason = suggestedSamples?.reason
  const transitions = transitionsFor(project.status)

  return (
    <div className="flex h-full flex-col">
      {/* Sticky-ish header: name + status + transitions + archive/restore */}
      <header className="flex flex-col gap-2 border-b border-border-subtle bg-surface-sunk px-6 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-role-page-title leading-tight">{project.name}</h2>
          <ProjectStatusChip status={project.status} />
          {isArchived ? <Chip size="xs" variant="neutral">Archived</Chip> : null}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-role-metadata">
          <span>
            {formulations.length} formulation{formulations.length === 1 ? '' : 's'}
          </span>
          <span className="opacity-40">·</span>
          <span>
            {sampleOrders.length} sample order{sampleOrders.length === 1 ? '' : 's'} linked
          </span>
          {project.updated_at ? (
            <>
              <span className="opacity-40">·</span>
              <span>updated {new Date(project.updated_at).toLocaleDateString()}</span>
            </>
          ) : null}
        </div>
        {!isArchived ? (
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {transitions.map((t) => {
              const Icon = t.icon
              return (
                <Button
                  key={t.to}
                  variant={t.variant}
                  size="sm"
                  onClick={() => setTransitionTarget(t)}
                  disabled={transitionMut.isPending}
                  title={`Status: ${project.status} → ${t.to}`}
                >
                  <Icon size={14} />
                  {t.label}
                </Button>
              )
            })}
            <span className="ml-auto" />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleArchive}
              disabled={archiveMut.isPending}
            >
              <Trash2 size={14} />
              Archive
            </Button>
          </div>
        ) : (
          <div className="mt-1 flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={handleRestore}
              loading={restoreMut.isPending}
            >
              <ArchiveRestore size={14} />
              Restore
            </Button>
          </div>
        )}
      </header>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="flex flex-col gap-6">
          {/* Header form */}
          <section className="flex max-w-xl flex-col gap-3">
            <h3 className="text-role-section-label">Details</h3>
            <FormField label="Customer">
              <PartnerPicker
                value={customer}
                onChange={setCustomer}
                kind="customer"
                placeholder="Search customers…"
                disabled={isArchived}
              />
            </FormField>
            <FormField label="Name" required>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isArchived}
                className="w-full rounded-xs border border-border-default bg-surface px-2 py-1.5 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none disabled:opacity-60"
              />
            </FormField>
            <FormField label="Description">
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                disabled={isArchived}
                className="w-full rounded-xs border border-border-default bg-surface px-2 py-1.5 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none disabled:opacity-60"
              />
            </FormField>
            {isHeaderDirty && !isArchived ? (
              <div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSave}
                  loading={updateMut.isPending}
                >
                  Save changes
                </Button>
              </div>
            ) : null}
          </section>

          {/* Formulations */}
          <section>
            <header className="mb-2 flex items-center justify-between gap-2">
              <h3 className="text-role-section-label">Formulations</h3>
              {!isArchived ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPickerOpen(true)}
                  disabled={linkFormulationMut.isPending}
                >
                  <Plus size={14} />
                  Link formulation
                </Button>
              ) : null}
            </header>
            {formulations.length === 0 ? (
              <p className="text-sm text-tertiary">
                No formulations attached yet. Use Link formulation to attach an
                existing formulation, or create a new one in the Formulations
                module first.
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-border-subtle border-y border-border-subtle">
                {formulations.map((f) => (
                  <li
                    key={f.id}
                    className="flex items-center gap-3 py-1.5"
                  >
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <div className="flex items-center gap-2">
                        {/* Sprint 11 — clickable formula_code routes to
                            the Formulations module where the per-
                            ingredient sourcing affordance lives.
                            Sprint 11 explicitly chose "per-formulation
                            drilldown" over a project-side bulk panel
                            for v1; bulk roll-up is parking-lot. */}
                        <Link
                          to="/formulations"
                          className="inline-flex items-center gap-1 text-role-data-primary text-link hover:underline"
                          title="Open in Formulations to see ingredients + sourcing"
                        >
                          {f.formula_code}
                          <ExternalLink size={10} />
                        </Link>
                        <ProjectStatusChip status={f.status} />
                      </div>
                      <span className="truncate text-role-data-secondary">{f.name}</span>
                    </div>
                    {!isArchived ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconOnly
                        aria-label={`Unlink ${f.formula_code}`}
                        onClick={() => handleUnlinkFormulation(f.id, f.formula_code)}
                        disabled={unlinkFormulationMut.isPending}
                      >
                        <Unlink size={14} />
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Sample orders */}
          <section>
            <h3 className="text-role-section-label mb-2">Sample orders</h3>
            {/* Linked zone */}
            {sampleOrders.length > 0 ? (
              <div className="mb-4">
                <div className="text-2xs uppercase tracking-widest text-tertiary mb-1">
                  Linked
                </div>
                <ul className="flex flex-col divide-y divide-border-subtle border-y border-border-subtle">
                  {sampleOrders.map((s) => (
                    <li
                      key={s.id}
                      className="flex items-center gap-3 py-1.5"
                    >
                      <a
                        href={odooUrl('sale.order', s.odoo_so_id)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-role-data-primary text-link hover:underline"
                      >
                        SO#{s.odoo_so_id}
                        <ExternalLink size={10} />
                      </a>
                      {s.notes ? (
                        <span className="text-role-data-secondary">{s.notes}</span>
                      ) : null}
                      {s.linked_at ? (
                        <span className="ml-auto text-role-metadata">
                          linked {new Date(s.linked_at).toLocaleDateString()}
                        </span>
                      ) : null}
                      {!isArchived ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconOnly
                          aria-label={`Unlink SO#${s.odoo_so_id}`}
                          onClick={() => handleUnlinkSample(s.id, s.odoo_so_id)}
                          disabled={unlinkSampleMut.isPending}
                        >
                          <Unlink size={14} />
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {/* Suggested zone */}
            <div>
              <div className="text-2xs uppercase tracking-widest text-tertiary mb-1">
                Suggested
              </div>
              {suggestionsList.length === 0 ? (
                <p className="text-sm text-tertiary">
                  {suggestionsReason ??
                    'No sample SOs found for this customer in the last 365 days.'}
                </p>
              ) : (
                <ul className="flex flex-col divide-y divide-border-subtle border-y border-border-subtle">
                  {suggestionsList.map((s) => (
                    <li
                      key={s.odoo_so_id}
                      className="flex items-center gap-3 py-1.5"
                    >
                      <a
                        href={odooUrl('sale.order', s.odoo_so_id)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-role-data-primary text-link hover:underline"
                      >
                        {s.name ?? `SO#${s.odoo_so_id}`}
                        <ExternalLink size={10} />
                      </a>
                      {s.date_order ? (
                        <span className="text-role-metadata">
                          {new Date(s.date_order).toLocaleDateString()}
                        </span>
                      ) : null}
                      <span className="ml-auto" />
                      {!isArchived ? (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleLinkSample(s.odoo_so_id)}
                          disabled={linkSampleMut.isPending}
                        >
                          Link
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          {/* Status history */}
          <section>
            <h3 className="text-role-section-label mb-2">Status history</h3>
            {statusHistory.length === 0 ? (
              <p className="text-sm text-tertiary">
                No transitions yet. The first status change writes the initial
                entry here.
              </p>
            ) : (
              <ul className="flex flex-col gap-1 text-sm">
                {statusHistory.map((h) => (
                  <li key={h.id} className="flex items-center gap-2">
                    <span>
                      {h.from_status ? (
                        <span className="text-role-data-secondary">
                          {h.from_status} →{' '}
                        </span>
                      ) : null}
                      <span className="text-role-data-primary">{h.to_status}</span>
                    </span>
                    {h.transitioned_by ? (
                      <span className="text-role-metadata">by {h.transitioned_by}</span>
                    ) : null}
                    {h.transitioned_at ? (
                      <span className="text-role-metadata">
                        {new Date(h.transitioned_at).toLocaleDateString()}
                      </span>
                    ) : null}
                    {h.comment ? (
                      <span className="text-role-data-secondary">— {h.comment}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      {/* Dialogs */}
      <ProjectTransitionDialog
        open={transitionTarget !== null}
        target={transitionTarget?.to ?? 'draft'}
        label={transitionTarget?.label ?? ''}
        confirmVariant={transitionTarget?.variant ?? 'primary'}
        onConfirm={handleTransitionConfirm}
        onCancel={() => setTransitionTarget(null)}
        pending={transitionMut.isPending}
      />
      {projectId !== null ? (
        <FormulationPicker
          open={pickerOpen}
          currentProjectId={projectId}
          excludeIds={linkedFormulationIds}
          onPick={handlePickFormulation}
          onCancel={() => setPickerOpen(false)}
          pending={linkFormulationMut.isPending}
        />
      ) : null}
    </div>
  )
}

// ─── helpers ─────────────────────────────────────────────────────────

function FormField({
  label,
  required,
  children,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-role-section-label">
        {label}
        {required ? <span className="ml-1 text-danger-text">*</span> : null}
      </span>
      {children}
    </label>
  )
}
